import { useState, useEffect, useCallback, useRef } from "react";
import { useTheme } from "../context/ThemeContext";

// --- CONFIGURATION BLOCK for Easy Remixing & Customization ---
export const CONFIG = {
  // Visuals
  primaryColor: "254, 59, 31",   // Crimson Red
  secondaryColor: "242, 50, 7", // Deep Red

  // Animation Speed (Higher value = slower animation)
  sphereRotationDuration: "240s", // Time for full sphere rotation
  gridPanDuration: "180s",        // Time for full background grid pan
  coreGlowDuration: "25s",        // Time for core light pulsation

  // Intensity & Depth
  wireframeOpacity: 0.5,         // Opacity of the wireframe lines
  wireframeShadowIntensity: 50,  // Glow size (in px) of the wireframe
  coreBlur: 200,                  // Blur radius (in px) of the core light
  parallaxDepth: 35,              // Strength of the mouse-follow effect (Higher = more movement)
  lerpFactor: 0.08,               // Smoothing factor for mouse movement
  sphereDensity: 12,              // Number of layered rings in the sphere (Higher = denser mesh)
  scrollSpeedFactor: 0.12,        // Multiplier to translate scrolling into degrees of rotation
};

// Helper function for linear interpolation (Lerp)
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

export default function InteractiveBackground() {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' ? window.innerWidth < 1024 : false);
  const isPerformanceRestricted = isMobile || isTouchDevice;
  
  const containerRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const hazeRef = useRef<HTMLDivElement>(null);
  const deepBaseRef = useRef<HTMLDivElement>(null);
  const bloomRef = useRef<HTMLDivElement>(null);
  const sphereMeshRef = useRef<HTMLDivElement>(null);
  const coreLightRef = useRef<HTMLDivElement>(null);
  
  const mousePos = useRef({ x: 0, y: 0 });
  const targetMousePos = useRef({ x: 0, y: 0 });
  const currentScroll = useRef(0);
  const targetScroll = useRef(0);
  
  const center = useRef({ x: 0, y: 0 });
  const animationFrameRef = useRef<number | undefined>(undefined);
  const lastMouseMoveTime = useRef(0);
  const lastTransforms = useRef({ grid: '', haze: '', base: '', sphere: '' });

  useEffect(() => {
    setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);

    const updateCenter = () => {
        center.current = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    };
    updateCenter();
    window.addEventListener('resize', updateCenter);
    
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
        window.removeEventListener('resize', updateCenter);
        window.removeEventListener('resize', checkMobile);
    };
  }, []);

  const animateLerp = useCallback(() => {
    // Disable intensive animation on restricted devices
    if (isPerformanceRestricted) return;
    
    // Smooth mouse position
    mousePos.current.x = lerp(mousePos.current.x, targetMousePos.current.x, CONFIG.lerpFactor);
    mousePos.current.y = lerp(mousePos.current.y, targetMousePos.current.y, CONFIG.lerpFactor);

    // Smooth scroll movement
    currentScroll.current = lerp(currentScroll.current, targetScroll.current, CONFIG.lerpFactor);

    // Apply styles imperatively
    const parallaxDepth = CONFIG.parallaxDepth;
    const rotationStrength = 5;

    const baseTranslate = `translate3d(${mousePos.current.x * parallaxDepth}px, ${mousePos.current.y * parallaxDepth}px, 0)`;
    const gridTranslate = `translate3d(${-mousePos.current.x * (parallaxDepth / 2)}px, ${-mousePos.current.y * (parallaxDepth / 2)}px, 0)`;
    const hazeTranslate = `translate3d(${mousePos.current.x * (parallaxDepth / 2)}px, ${-mousePos.current.y * (parallaxDepth / 2)}px, 0)`;
    
    const scrollRotationDeg = currentScroll.current * CONFIG.scrollSpeedFactor;
    const tiltRotateX = (mousePos.current.y * rotationStrength) + (scrollRotationDeg * 0.45);
    const tiltRotateY = (-mousePos.current.x * rotationStrength) + (scrollRotationDeg * 0.65);
    const tiltRotateZ = scrollRotationDeg * 0.25;
    const tiltTranslate = `rotateX(${tiltRotateX}deg) rotateY(${tiltRotateY}deg) rotateZ(${tiltRotateZ}deg)`;

    if (gridRef.current && lastTransforms.current.grid !== gridTranslate) {
        gridRef.current.style.transform = gridTranslate;
        lastTransforms.current.grid = gridTranslate;
    }
    if (hazeRef.current && lastTransforms.current.haze !== hazeTranslate) {
        hazeRef.current.style.transform = hazeTranslate;
        lastTransforms.current.haze = hazeTranslate;
    }
    if (deepBaseRef.current && lastTransforms.current.base !== baseTranslate) {
        deepBaseRef.current.style.transform = baseTranslate;
        lastTransforms.current.base = baseTranslate;
    }
    if (bloomRef.current && lastTransforms.current.base !== baseTranslate) {
        bloomRef.current.style.transform = baseTranslate;
    }
    if (sphereMeshRef.current && lastTransforms.current.sphere !== tiltTranslate) {
        sphereMeshRef.current.style.transform = tiltTranslate;
        lastTransforms.current.sphere = tiltTranslate;
    }
    
    animationFrameRef.current = requestAnimationFrame(animateLerp);
  }, [isPerformanceRestricted]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (isPerformanceRestricted) return;
    const now = Date.now();
    if (now - lastMouseMoveTime.current < 32) return; 
    lastMouseMoveTime.current = now;
    
    const { clientX, clientY } = e;
    targetMousePos.current = { 
        x: (clientX - center.current.x) / center.current.x, 
        y: (clientY - center.current.y) / center.current.y 
    };
  }, [isPerformanceRestricted]);

  const handleScroll = useCallback(() => {
    if (isPerformanceRestricted) return;
    targetScroll.current = window.scrollY || document.documentElement.scrollTop;
  }, [isPerformanceRestricted]);

  useEffect(() => {
    if (isPerformanceRestricted) {
        if (gridRef.current) gridRef.current.style.transform = 'none';
        if (hazeRef.current) hazeRef.current.style.transform = 'none';
        if (deepBaseRef.current) deepBaseRef.current.style.transform = 'none';
        if (sphereMeshRef.current) sphereMeshRef.current.style.transform = 'none';
        if (bloomRef.current) bloomRef.current.style.transform = 'none';
    }
  }, [isPerformanceRestricted]);

  useEffect(() => {
    if (!isPerformanceRestricted) {
      animationFrameRef.current = requestAnimationFrame(animateLerp);
    }
    return () => {
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    };
  }, [animateLerp, isPerformanceRestricted]);

  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove, { passive: true });
    window.addEventListener("scroll", handleScroll, { passive: true });
    
    targetScroll.current = window.scrollY;
    
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [handleMouseMove, handleScroll]);

  // Generate sphere rings (only if NOT performance restricted)
  const sphereRings = isPerformanceRestricted ? null : Array.from({ length: CONFIG.sphereDensity }, (_, i) => {
    const step = 90 / (CONFIG.sphereDensity / 2);
    const angle = i * step;
    return (
      <div
        key={`ring-${i}`}
        className="wireframe-line"
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: "50%",
          border: `1px solid rgba(${CONFIG.primaryColor}, ${isDark ? CONFIG.wireframeOpacity : 0.4})`,
          transform: i % 2 === 0 ? `rotateY(${angle}deg)` : `rotateX(${angle}deg)`,
          boxShadow: `0 0 ${CONFIG.wireframeShadowIntensity}px rgba(${CONFIG.primaryColor}, ${isDark ? 0.05 : 0.08})`,
        }}
        aria-hidden="true"
      />
    );
  });

  // Styles (simplified for restricted performance)
  const coreLightStyle = {
    width: "400px",
    height: "400px",
    backgroundImage: isDark 
      ? `radial-gradient(circle, rgba(${CONFIG.secondaryColor}, 0.45) 0%, transparent 70%)`
      : `radial-gradient(circle, rgba(${CONFIG.secondaryColor}, 0.22) 0%, transparent 70%)`,
    filter: `blur(${isPerformanceRestricted ? 50 : CONFIG.coreBlur}px)`,
    boxShadow: isDark
      ? `0 0 ${isPerformanceRestricted ? 25 : CONFIG.coreBlur / 2}px 30px rgba(${CONFIG.secondaryColor}, 0.2), 0 0 ${isPerformanceRestricted ? 50 : CONFIG.coreBlur}px 50px rgba(${CONFIG.primaryColor}, 0.15)`
      : `0 0 ${isPerformanceRestricted ? 25 : CONFIG.coreBlur / 2}px 30px rgba(${CONFIG.secondaryColor}, 0.06), 0 0 ${isPerformanceRestricted ? 50 : CONFIG.coreBlur}px 50px rgba(${CONFIG.primaryColor}, 0.04)`,
  };

  const panningGridStyle = {
    backgroundImage: isDark
      ? "repeating-linear-gradient(to right, rgba(10,10,10,0.9) 1px, transparent 1px), repeating-linear-gradient(to bottom, rgba(10,10,10,0.9) 1px, transparent 1px)"
      : "repeating-linear-gradient(to right, rgba(200,200,200,0.18) 1px, transparent 1px), repeating-linear-gradient(to bottom, rgba(200,200,200,0.18) 1px, transparent 1px)",
    backgroundSize: "40px 40px",
    opacity: isDark ? 0.15 : 0.45,
  };

  const hazeStyle = {
    backgroundImage: isDark
      ? `radial-gradient(circle at 50% 50%, rgba(${CONFIG.primaryColor}, 0.15) 0%, transparent 50%)`
      : `radial-gradient(circle at 50% 50%, rgba(${CONFIG.primaryColor}, 0.06) 0%, transparent 50%)`,
    filter: `blur(${isPerformanceRestricted ? 50 : 150}px)`,
    opacity: isDark ? 0.6 : 0.4,
    mixBlendMode: isPerformanceRestricted ? 'normal' : (isDark ? 'screen' : 'multiply'),
  };

  const deepBaseStyle = {
    backgroundImage: isDark
      ? `radial-gradient(at 50% 50%, rgba(${CONFIG.primaryColor}, 0.08) 0%, #0c0b0b 90%)`
      : `radial-gradient(at 50% 50%, rgba(${CONFIG.primaryColor}, 0.04) 0%, #fdfdfd 90%)`,
  };

  const bloomStyle = {
    backgroundImage: isDark
      ? `radial-gradient(circle at 50% 50%, rgba(${CONFIG.primaryColor}, 0.35) 0%, transparent 50%), radial-gradient(circle at 10% 10%, rgba(${CONFIG.secondaryColor}, 0.25) 0%, transparent 30%)`
      : `radial-gradient(circle at 50% 50%, rgba(${CONFIG.primaryColor}, 0.1) 0%, transparent 50%), radial-gradient(circle at 10% 10%, rgba(${CONFIG.secondaryColor}, 0.05) 0%, transparent 30%)`,
    mixBlendMode: isPerformanceRestricted ? 'normal' : (isDark ? 'screen' : 'multiply'),
    filter: `blur(${isPerformanceRestricted ? 50 : 100}px)`,
    opacity: isDark ? 0.95 : 0.6,
  };

  // Note: For mobile/tablets, you could insert an image here as a background-image in the container style
  // e.g., backgroundImage: isPerformanceRestricted ? 'url("/path/to/static-bg.jpg")' : 'none'

  return (
    <div ref={containerRef} className={`fixed inset-0 w-full h-full pointer-events-none z-[-1] overflow-hidden ${isDark ? 'bg-[#0c0b0b]' : 'bg-[#fdfdfd]'} transition-colors duration-300`}>
      {/* Layer 0: Panning Grid Layer */}
      <div ref={gridRef} className="absolute inset-0 transition-transform duration-0 ease-linear" style={{ ...panningGridStyle, willChange: 'transform' }} />

      {/* Layer 1: Volumetric Haze */}
      <div ref={hazeRef} className="absolute inset-0 transition-transform duration-0 ease-linear" style={{ ...hazeStyle, willChange: 'transform' }} />

      {/* Layer 2: Deep Base Background & Core Glow */}
      <div ref={deepBaseRef} className="absolute inset-0 transition-transform duration-0 ease-linear" style={{ ...deepBaseStyle, willChange: 'transform', backfaceVisibility: 'hidden' }}>
        <div 
          ref={coreLightRef}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full pointer-events-none w-[400px] h-[400px] animate-pulse-core" 
          style={coreLightStyle} 
        />
      </div>

      {/* Layer 3: Interactive 3D Sphere mesh (disabled on restricted) */}
      {!isPerformanceRestricted && (
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none select-none z-40">
          <div
            ref={sphereMeshRef}
            className="w-[300px] h-[300px] md:w-[750px] md:h-[750px] lg:w-[900px] lg:h-[900px] xl:w-[1100px] xl:h-[1100px] relative transition-transform duration-0 ease-linear"
            style={{
              transformOrigin: "center center",
              transformStyle: "preserve-3d",
              willChange: "transform",
              backfaceVisibility: "hidden",
            }}
          >
            {sphereRings}
          </div>
        </div>
      )}

      {/* Layer 4: Soft Radial Bloom (Ambient Light Layer) */}
      <div ref={bloomRef} className="absolute inset-0 pointer-events-none transition-transform duration-0 ease-linear" style={{ ...bloomStyle, willChange: 'transform' }} />

      {/* Layer 5: Noise Layer (For Film Grain Texture) */}
      <div
        className="absolute inset-0 pointer-events-none mix-blend-overlay opacity-20"
        style={{
          backgroundImage: 'url("https://framerusercontent.com/images/g0QcWrxr87K0ufOxIUFBakwYA8.png")',
          backgroundSize: "200px",
        }}
      />

      {/* Layer 6: Vignette overlay */}
      <div 
        className="absolute inset-0 pointer-events-none transition-all duration-300" 
        style={{
          backgroundImage: isDark
            ? "radial-gradient(circle at center, transparent 20%, #050505 95%)"
            : "radial-gradient(circle at center, transparent 35%, rgba(240,238,236,0.8) 95%)"
        }}
      />
    </div>
  );
}
